import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material'
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material'

import { BlueprintFamilyExampleImage } from '../../../../api/blueprintFamilies/exampleImages'
import { GRModal } from '../../../../components/GRModal/GRModal'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { LiveEventSourceCaption } from '../../../../components/LiveEventSourceCaption/LiveEventSourceCaption'

interface BlueprintScreenshotLightboxProps {
  // The shots the lightbox pages through (callers leave out the ones the reader can't open).
  images: BlueprintFamilyExampleImage[]
  // Position within `images`; `null` keeps the lightbox closed.
  index: number | null
  onIndexChange: (index: number) => void
  onClose: () => void
  ariaLabel: string
  // Alt text for the enlarged shot. Receives the shot and its position within `images`.
  getImageAlt: (image: BlueprintFamilyExampleImage, index: number) => string
}

const navButtonSx = {
  color: 'text.secondary',
  backgroundColor: 'background.paper',
  '&:hover': { color: 'primary.contrastText', backgroundColor: 'primary.main' },
} as const

/**
 * Enlarged-screenshot lightbox for Blueprint Family example images. Shared by the Live Event
 * Analytics detail page and the Data Glossary Blueprint Families tab so both show the same
 * chrome: no header, close / previous / next buttons with tooltips, and a footer with the source
 * game + event next to the "n / total" counter. Prev/next wrap around at the ends.
 */
export const BlueprintScreenshotLightbox: React.FC<BlueprintScreenshotLightboxProps> = ({ images, index, onIndexChange, onClose, ariaLabel, getImageAlt }) => {
  const { t } = useTranslation()
  const count = images.length
  const hasNav = count > 1
  const current = index !== null ? images[index] : undefined

  const goToPrevious = useCallback(() => {
    if (index === null || count === 0) return
    onIndexChange((index - 1 + count) % count)
  }, [index, count, onIndexChange])
  const goToNext = useCallback(() => {
    if (index === null || count === 0) return
    onIndexChange((index + 1) % count)
  }, [index, count, onIndexChange])

  // ArrowLeft/ArrowRight navigation while the lightbox is open.
  useEffect(() => {
    if (index === null || !hasNav) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        goToPrevious()
      } else if (event.key === 'ArrowRight') {
        goToNext()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [index, hasNav, goToPrevious, goToNext])

  return (
    <GRModal open={index !== null} size="large" onClose={onClose} ariaLabel={ariaLabel}>
      <Box sx={{ position: 'relative', p: 2 }}>
        <GRTooltip content={t('common:close')} styles={{ position: 'absolute', top: 8, right: 8, zIndex: 1, display: 'inline-flex' }}>
          <IconButton aria-label={t('common:close')} onClick={onClose} sx={navButtonSx}>
            <Close />
          </IconButton>
        </GRTooltip>
        {hasNav ? (
          <>
            <GRTooltip
              content={t('common:label_previous')}
              styles={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', zIndex: 1, display: 'inline-flex' }}
            >
              <IconButton aria-label={t('common:label_previous')} onClick={goToPrevious} sx={navButtonSx}>
                <ChevronLeft />
              </IconButton>
            </GRTooltip>
            <GRTooltip
              content={t('common:label_next')}
              styles={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', zIndex: 1, display: 'inline-flex' }}
            >
              <IconButton aria-label={t('common:label_next')} onClick={goToNext} sx={navButtonSx}>
                <ChevronRight />
              </IconButton>
            </GRTooltip>
          </>
        ) : null}
        {index !== null && current ? (
          <>
            <Box
              component="img"
              src={current.url}
              alt={getImageAlt(current, index)}
              sx={{ display: 'block', mx: 'auto', maxWidth: '100%', maxHeight: 'calc(100vh - 136px)', objectFit: 'contain' }}
            />
            {/* Source and position share one row under the image. The counter used
                to float over the bitmap's bottom edge; with a caption below it that
                would land on top of the caption, and there is room for both here. */}
            {/* Footer divider spans the modal edge to edge (cancels the container's p: 2). */}
            <Divider sx={{ mt: 2, mx: -2 }} />
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ mt: 1.5, minWidth: 0 }}>
              <LiveEventSourceCaption
                image={current}
                variant="body1"
                showGameIcon
                stacked
                hideTooltip
                styles={{ width: 'auto', gap: 1.5, color: 'primary.main' }}
              />
              {hasNav ? (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Typography variant="body1" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    {t('market-analytics:lightbox_index_of_total', { index: index + 1, total: count })}
                  </Typography>
                </>
              ) : null}
            </Stack>
          </>
        ) : null}
      </Box>
    </GRModal>
  )
}
